// Convert a CoLOS-Designer-style label (what the editor writes) into the form the SmartDate X65 itself
// writes and loads: no header comment, no <AppInfo> property bags, fonts sized with <PointSizeInHM>
// (em size, 1/100 mm) and chosen by font file (arial.ttf / arialbd.ttf) instead of CharHeight/CharWidth/Bold.
// Derived from the printer-accepted files in tests/fixtures/machine. Converting a machine file is a no-op.
// Pure module: no DOM.
import { parseXml, serializeXml, child, childrenOf, textOf, setText, elem, removeNode } from './xml.js';
import { CELL_TO_EM } from '../render/metrics.js';

const isElement = (n) => n && n.type === 'element';

function walk(el, fn) {
  fn(el);
  for (const c of [...el.children]) if (isElement(c)) walk(c, fn);
}

/**
 * @param {string} xmlText serialized label XML
 * @param {{deviceInstance?: string|null}} [opts] when given, DeviceName is blanked like printer files do
 * @returns {{ text: string, warnings: string[] }}
 */
export function toMachineText(xmlText, { deviceInstance = null } = {}) {
  const doc = parseXml(xmlText);
  const warnings = [];
  doc.children = doc.children.filter((c) => c.type !== 'comment');
  const root = doc.children.find(isElement);
  if (!root) return { text: xmlText, warnings };
  walk(root, (el) => {
    for (const c of childrenOf(el, 'AppInfo')) removeNode(el, c);
    if (el.name !== 'Font') return;
    const heightEl = child(el, 'CharHeight');
    if (!heightEl) return; // already in printer form
    const face = textOf(child(el, 'Typeface'));
    const flag = (n) => textOf(child(el, n)) === 'true';
    if (face !== 'Arial') {
      warnings.push(`Font “${face}” kept in CoLOS form (no printer sample for it)`);
      return;
    }
    if (flag('Italic') || flag('Underline')) {
      warnings.push('Italic/underline text kept in CoLOS form (the printer files have no sample of it)');
      return;
    }
    const ps = Math.max(1, Math.round(parseInt(textOf(heightEl), 10) / CELL_TO_EM));
    const file = el.children.find((c) => isElement(c) && c.name === 'Filename');
    const keepFile = file && textOf(file) && !/^arial(bd)?\.ttf$/i.test(textOf(file)) ? textOf(file) : null;
    el.children = [
      elem('Typeface', {}, face),
      elem('PointSizeInHM', {}, String(ps)),
      elem('Filename', {}, keepFile || (flag('Bold') ? 'arialbd.ttf' : 'arial.ttf')),
    ];
    el.selfClosing = false;
  });
  if (deviceInstance) {
    const name = child(child(root, 'DeviceInfo'), 'DeviceName');
    if (name) setText(name, '');
  }
  return { text: serializeXml(doc), warnings };
}
